// Хуки для управления пользователями
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import * as usersApi from '@/api/users'

export const useUsers = (params: usersApi.UserListParams) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => usersApi.fetchUsers(params),
    placeholderData: (prev) => prev,
  })
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: usersApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      message.success('Сотрудник добавлен')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка добавления сотрудника')
    },
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: usersApi.UserUpdatePayload }) =>
      usersApi.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      message.success('Данные сотрудника обновлены')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка обновления')
    },
  })
}

export const useDeactivateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: usersApi.deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      message.success('Доступ сотрудника закрыт')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка деактивации')
    },
  })
}

export const useReactivateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: usersApi.reactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      message.success('Доступ сотрудника открыт')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка активации')
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: usersApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      message.success('Сотрудник удалён')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления')
    },
  })
}

export const useAnonymizeUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: usersApi.anonymizeUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      message.success('PII сотрудника anonymized; аккаунт деактивирован')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка анонимизации')
    },
  })
}

export const useOrganizations = () => {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: usersApi.fetchOrganizations,
  })
}

export const useUserAccessGroups = (userId: string | null) => {
  return useQuery({
    queryKey: ['user-access-groups', userId],
    queryFn: () => usersApi.fetchUserAccessGroups(userId!),
    enabled: !!userId,
  })
}

export const useUserDashboardAccess = (userId: string | null) => {
  return useQuery({
    queryKey: ['user-dashboard-access', userId],
    queryFn: () => usersApi.fetchUserDashboardAccess(userId!),
    enabled: !!userId,
  })
}

export const useCreateUserDashboardGrant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: usersApi.UserDashboardAccessExceptionCreatePayload }) =>
      usersApi.createUserDashboardGrant(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', userId] })
      message.success('Direct grant добавлен')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка добавления direct grant')
    },
  })
}

export const useDeleteUserDashboardGrant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, exceptionId }: { userId: string; exceptionId: string }) =>
      usersApi.deleteUserDashboardGrant(userId, exceptionId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', userId] })
      message.success('Direct grant удален')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления direct grant')
    },
  })
}

export const useCreateUserDashboardDeny = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: usersApi.UserDashboardAccessExceptionCreatePayload }) =>
      usersApi.createUserDashboardDeny(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', userId] })
      message.success('Direct deny добавлен')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка добавления direct deny')
    },
  })
}

export const useDeleteUserDashboardDeny = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, exceptionId }: { userId: string; exceptionId: string }) =>
      usersApi.deleteUserDashboardDeny(userId, exceptionId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-dashboard-access', userId] })
      message.success('Direct deny удален')
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Ошибка удаления direct deny')
    },
  })
}
